import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/AxiosInstance';
import CorporateSubscription from '../user subscriptions/coporatesubscription';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import { clearSubscriptionCache } from '../../utils/subscriptionCache';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import { formatFlowdoPlanWithModule } from '../../utils/subscriptionPlanLabels';
import './UserSubscription.css';
import './SubscriptionCheckout.css';
import './RegistrationSubscriptionPlans.css';
import { Footer } from '../components';

const REGISTRATION_PLANS_STORAGE = 'registrationSelectedPlans';

function getPlanPriceForCycle(plan, cycle) {
  return parseFloat(cycle === 'yearly' ? plan?.yearly_price ?? 0 : plan?.monthly_price ?? 0) || 0;
}

function isPlanFree(plan) {
  return getPlanPriceForCycle(plan, 'monthly') === 0 && getPlanPriceForCycle(plan, 'yearly') === 0;
}

function persistRegistrationCart(map) {
  try {
    sessionStorage.setItem(REGISTRATION_PLANS_STORAGE, JSON.stringify(map));
  } catch (e) {}
}

function moduleIdFromPlan(plan) {
  return plan?.module_id ?? plan?.Module?.id ?? null;
}

const RegistrationSubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedByModuleId, setSelectedByModuleId] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const confirmSectionRef = useRef(null);

  const selectedList = useMemo(() => Object.values(selectedByModuleId), [selectedByModuleId]);

  const initialModuleTab = useMemo(() => {
    const m = new URLSearchParams(location.search).get('module');
    if (m === 'document' || m === 'task') return m;
    if (m === 'credentialvault') return 'credential vault';
    return null;
  }, [location.search]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axiosInstance.get('/subscription/plans');
      if (response.data?.data) {
        return sortSubscriptionPlansBySortOrder([...response.data.data]);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
    return [];
  };

   useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasQuery = params.toString().length > 0;

    // From registration: `/registration-subscription` with no ?plan / ?module — start with no pre-selected plans
    if (!hasQuery) {
      try {
        sessionStorage.removeItem(REGISTRATION_PLANS_STORAGE);
        sessionStorage.removeItem('selectedPlan');
      } catch (e) {}
      setSelectedByModuleId({});
      return;
    }

    try {
      const raw = sessionStorage.getItem(REGISTRATION_PLANS_STORAGE);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setSelectedByModuleId(parsed);
          return;
        }
      }
      const legacy = sessionStorage.getItem('selectedPlan');
      if (legacy) {
        const parsed = JSON.parse(legacy);
        const mid = moduleIdFromPlan(parsed);
        if (mid != null) {
          setSelectedByModuleId({ [String(mid)]: parsed });
        }
      }
    } catch (e) {
      console.error('Error restoring registration cart:', e);
    }
  }, [location.search]);

  useEffect(() => {
    const syncPlanFromUrl = async () => {
      const urlParams = new URLSearchParams(location.search);
      const planId = urlParams.get('plan');
      if (!planId) return;

      const plans = await fetchSubscriptionPlans();
      const planToSelect = plans.find((p) => String(p.id) === String(planId));
      if (!planToSelect) return;

      const mid = moduleIdFromPlan(planToSelect);
      if (mid == null) return;

      setSelectedByModuleId((prev) => {
        const next = { ...prev, [String(mid)]: planToSelect };
        persistRegistrationCart(next);
        return next;
      });
    };

    syncPlanFromUrl();
  }, [location.search]);

  useEffect(() => {
    if (!selectedList.length) return;
    const t = setTimeout(() => {
      confirmSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedList.length]);

  const cartHasPaid = selectedList.some((p) => !isPlanFree(p));
  const cartHasFree = selectedList.some((p) => isPlanFree(p));
  const totalDueToday = useMemo(
    () => selectedList.reduce((sum, p) => sum + getPlanPriceForCycle(p, billingCycle), 0),
    [selectedList, billingCycle]
  );

  const removeModuleSelection = (moduleKey) => {
    setSelectedByModuleId((prev) => {
      const next = { ...prev };
      delete next[String(moduleKey)];
      persistRegistrationCart(next);
      return next;
    });
  };

  const handleProceedToCheckout = () => {
    if (!selectedList.length) {
      toast.error('Please select at least one subscription plan');
      return;
    }
    if (cartHasPaid && cartHasFree) {
      toast.error(
        'Choose paid plans for checkout or free plans for instant activation—not both in one selection.'
      );
      return;
    }
    if (!cartHasPaid) {
      toast.error('This selection is free. Use Confirm Free Subscription instead.');
      return;
    }

    try {
      localStorage.setItem(
        'subscription_checkout_plan',
        JSON.stringify({
          lineItems: selectedList.map((p) => ({
            planId: p.id || p.planId,
            billing_cycle: billingCycle,
          })),
          billingCycle,
          checkoutIntent: 'new',
        })
      );
    } catch (e) {
      console.error('Failed to save checkout cart:', e);
    }

    navigate(`/subscription/checkout?billingCycle=${billingCycle}&checkoutIntent=new`);
  };

  const handleSubscribeFree = async () => {
    if (!selectedList.length) {
      toast.error('Please select at least one subscription plan');
      return;
    }
    if (!cartHasFree || cartHasPaid) {
      toast.error('Select only free plans, or use Proceed to Checkout for paid plans.');
      return;
    }

    setIsSubscribing(true);
    try {
      for (const p of selectedList) {
        await axiosInstance.post('/userSubscription/subscribe', {
          subscription_plan_id: p.planId || p.id,
        });
      }

      toast.success(
        selectedList.length > 1
          ? 'Successfully subscribed to all selected plans!'
          : 'Successfully subscribed to the plan!'
      );
      sessionStorage.removeItem('selectedPlan');
      sessionStorage.removeItem(REGISTRATION_PLANS_STORAGE);
      setSelectedByModuleId({});
      clearSubscriptionCache();
      clearFeatureCache();
      try {
        window.dispatchEvent(new Event('user-subscription-updated'));
      } catch (e) {}
      try {
        const featuresResponse = await axiosInstance.get('/userSubscription/features');
        if (featuresResponse.data?.features) {
          localStorage.setItem('userPlanFeatures', JSON.stringify(featuresResponse.data.features));
        }
      } catch (err) {
        console.error('Error refreshing user features:', err);
      }
      setTimeout(() => navigate('/task/tasklist'), 1500);
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleSkipForNow = () => {
    sessionStorage.removeItem('selectedPlan');
    sessionStorage.removeItem(REGISTRATION_PLANS_STORAGE);
    toast.info('You can select a subscription plan later from your dashboard');
    navigate('/task/tasklist');
  };

  return (
    <div className="subscription-page registration-subscription-page">
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12">
              <div className="row">
                <div className="col-xl-12">
                  <div className="card border-0 shadow-sm main-card">
                    <div className="card-body p-0">
                      <div className="registration-subscription-content-wrap">
                        <div className="registration-subscription-intro">
                          <h4 className="mb-2 fw-bold">Choose your plan</h4>
                          <p className="mb-0">
                            Select a subscription to unlock features. Paid plans go through checkout and the payment
                            gateway. You can skip and subscribe later from My Subscription.
                          </p>
                        </div>

                        <CorporateSubscription
                          activePlanId={null}
                          parentSelectedPlan={null}
                          allowMultiModuleCart
                          suppressModulePopupOnSelect
                          selectedPlansByModuleId={selectedByModuleId}
                          onPlanSelect={(plan) => {
                            const mid = moduleIdFromPlan(plan);
                            if (mid == null) {
                              toast.error('Could not determine product for this plan.');
                              return;
                            }
                            setSelectedByModuleId((prev) => {
                              const next = { ...prev, [String(mid)]: { ...plan } };
                              persistRegistrationCart(next);
                              return next;
                            });
                            try {
                              sessionStorage.setItem('selectedPlan', JSON.stringify(plan));
                            } catch (e) {}
                          }}
                          onBillingCycleChange={setBillingCycle}
                          onModuleTabChange={() => {}}
                          initialModuleTab={initialModuleTab}
                        />

                        {selectedList.length > 0 && (
                          <div ref={confirmSectionRef} className="confirm-section mt-4 mb-4">
                            <div className="w-100">
                              <div className="subscription-confirm-pricing subscription-confirm-detail-layout text-start mb-4 w-100">
                                {selectedList.map((p, idx) => {
                                  const mid = moduleIdFromPlan(p);
                                  const label = formatFlowdoPlanWithModule(p);
                                  return (
                                    <div
                                      key={`${mid}-${p.id}`}
                                      className={idx > 0 ? 'registration-confirm-plan-follow pt-3 mt-2' : ''}
                                    >
                                      <Row className="g-3 subscription-confirm-pricing-cols">
                                        <Col xs={12} md={6} className="subscription-confirm-col-left">
                                          <div className="checkout-detail-rows">
                                            <div className="checkout-detail-row">
                                              <span>Selected plan:</span>
                                              <span className="fw-semibold checkout-detail-value">{label}</span>
                                            </div>
                                            <div className="checkout-detail-row">
                                              <span>Period:</span>
                                              <span className="fw-semibold checkout-detail-value">
                                                {billingCycle === 'yearly' ? '1 year' : '1 month'}
                                              </span>
                                            </div>
                                          </div>
                                        </Col>
                                        <Col xs={12} md={6} className="subscription-confirm-col-right">
                                          <div className="checkout-detail-rows">
                                            <div className="checkout-detail-row">
                                              <span>Plan amount:</span>
                                              <span className="fw-semibold checkout-detail-value">
                                                $
                                                {isPlanFree(p)
                                                  ? '0.00'
                                                  : getPlanPriceForCycle(p, billingCycle).toFixed(2)}
                                              </span>
                                            </div>
                                            {mid != null && (
                                              <div className="mt-2">
                                                <button
                                                  type="button"
                                                  className="btn btn-link btn-sm text-danger p-0 registration-cart-remove"
                                                  onClick={() => removeModuleSelection(mid)}
                                                >
                                                  Remove this plan
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  );
                                })}
                                <div className="subscription-confirm-total-block w-100">
                                  <Row className="g-3 subscription-confirm-total-row">
                                    <Col xs={12} md={6} className="d-none d-md-block" aria-hidden="true" />
                                    <Col xs={12} md={6}>
                                      <div className="checkout-detail-row subscription-confirm-total-inner">
                                        <span className="fw-bold">Total due today:</span>
                                        <span className="fw-bold checkout-detail-value">
                                          {cartHasPaid ? `$${totalDueToday.toFixed(2)}` : '$0.00'}
                                        </span>
                                      </div>
                                      {cartHasPaid && selectedList.length > 1 && (
                                        <p className="small text-muted mb-0 mt-1">
                                          Final amount may differ slightly after taxes or proration; shown again at
                                          checkout.
                                        </p>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                              <div className="text-center">
                                {cartHasFree && !cartHasPaid ? (
                                  <Button
                                    variant="danger"
                                    size="lg"
                                    onClick={handleSubscribeFree}
                                    disabled={isSubscribing}
                                    className="px-5 py-3 confirm-button"
                                  >
                                    {isSubscribing ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Processing...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-check me-2" />
                                        Confirm Free Subscription
                                      </>
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="danger"
                                    size="lg"
                                    onClick={handleProceedToCheckout}
                                    className="px-5 py-3 confirm-button"
                                  >
                                    <i className="fas fa-shopping-cart me-2" />
                                    Proceed to Checkout
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="text-center pb-3">
                          <Button
                            variant="outline-danger"
                            size="lg"
                            className="px-4 registration-skip-btn"
                            onClick={handleSkipForNow}
                          >
                            <i className="fas fa-clock me-2" />
                            Skip for now
                          </Button>
                        </div>

                        <div className="terms-section-registration pb-4 text-center">
                          <p className="text-muted small mb-0">
                            By continuing, you agree to our{' '}
                            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                              Terms and Conditions
                            </a>{' '}
                            and{' '}
                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                              Privacy Policy
                            </a>
                            .
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegistrationSubscriptionPlans;
